import React, { useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ExternalLink, Pencil, Sparkles } from 'lucide-react';
import { sanitizeText } from '../utils/sanitization';

// Editable cell component with unified editing state management
const EditableCell = ({
  value,
  fieldId,
  product,
  rowIndex,
  isEditing,
  editingCell,
  readOnly,
  onCellEdit,
  onCellValueChange,
  onCellKeyPress,
  onCellSave,
  onProductIdClick,
  calculateColumnValue,
  isCalculatedColumn,
  taxRate,
  canViewPrices = true
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle calculated columns
  let displayValue = value;
  if (isCalculatedColumn(fieldId) && product) {
    displayValue = calculateColumnValue(fieldId, product, { taxRate });
  }

  // Fallback for price field when value is missing but unit_cost exists
  if ((displayValue === null || displayValue === undefined) && fieldId === 'price' && product?.unit_cost !== undefined) {
    displayValue = product.unit_cost;
  }

  if (displayValue === null || displayValue === undefined) {
    // client_ref_number (物料编码) must stay clickable in edit mode even when empty
    if (fieldId === 'client_ref_number' && !readOnly) {
      displayValue = '';
    } else {
      return '-';
    }
  }

  switch (fieldId) {
    case 'price':
    case 'unit_price':
      // Hide price for users without permission
      if (!canViewPrices) {
        return <span className="text-muted-foreground">***</span>;
      }
      // Special handling for editable price with unified editing state
      if (!readOnly && isEditing) {
        return (
          <div className="relative w-[120px]">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">¥</span>
            <Input
              type="text"
              inputMode="decimal"
              value={editingCell?.value ?? ''}
              onChange={(e) => onCellValueChange(e.target.value)}
              onKeyDown={onCellKeyPress}
              onBlur={onCellSave}
              ref={inputRef}
              className="h-8 pl-7 text-right"
            />
          </div>
        );
      }
      return (
        <div
          onClick={() => !readOnly && !editingCell && onCellEdit(rowIndex, fieldId, displayValue)}
          className={`flex items-center gap-1 p-1 rounded h-8 min-h-[32px]
            ${readOnly || editingCell ? '' : 'cursor-pointer hover:bg-muted'}
            ${editingCell && !isEditing ? 'opacity-50' : ''}`}
        >
          <span className="text-sm">
            ¥{displayValue}
          </span>
          {!readOnly && !editingCell && (
            <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
          )}
        </div>
      );

    case 'unit_price_tax_included':
      // Hide price for users without permission
      if (!canViewPrices) {
        return <span className="text-muted-foreground">***</span>;
      }
      // Special handling for editable unit price with tax included
      if (!readOnly && isEditing) {
        return (
          <div className="relative w-[120px]">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">¥</span>
            <Input
              type="text"
              inputMode="decimal"
              value={editingCell?.value ?? ''}
              onChange={(e) => onCellValueChange(e.target.value)}
              onKeyDown={onCellKeyPress}
              onBlur={onCellSave}
              ref={inputRef}
              className="h-8 pl-7 text-right"
            />
          </div>
        );
      }
      return (
        <div
          onClick={() => !readOnly && !editingCell && onCellEdit(rowIndex, 'unit_price_tax_included', displayValue)}
          className={`flex items-center gap-1 p-1 rounded h-8 min-h-[32px]
            ${readOnly || editingCell ? '' : 'cursor-pointer hover:bg-muted'}
            ${editingCell && !isEditing ? 'opacity-50' : ''}`}
        >
          <span className="text-sm">
            ¥{displayValue}
          </span>
          {!readOnly && !editingCell && (
            <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
          )}
        </div>
      );

    case 'total_price':
    case 'total_price_tax_included':
      // Hide price for users without permission
      if (!canViewPrices) {
        return <span className="text-muted-foreground">***</span>;
      }
      return (
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium text-primary">
            ¥{typeof displayValue === 'number' ? displayValue.toFixed(2) : displayValue}
          </span>
          {isCalculatedColumn(fieldId) && (
            <Sparkles className="h-3 w-3 text-amber-500" />
          )}
        </div>
      );

    case 'quantity':
      // Special handling for editable quantity with unified editing state
      if (!readOnly && isEditing) {
        return (
          <Input
            type="text"
            inputMode="numeric"
            value={editingCell?.value ?? ''}
            onChange={(e) => onCellValueChange(e.target.value)}
            onKeyDown={onCellKeyPress}
            onBlur={onCellSave}
            ref={inputRef}
            className="h-8 w-20 text-center"
          />
        );
      }
      return (
        <div
          onClick={() => !readOnly && !editingCell && onCellEdit(rowIndex, 'quantity', displayValue)}
          className={`flex items-center justify-center p-1 rounded min-w-[60px] h-8 min-h-[32px]
            ${readOnly || editingCell ? '' : 'cursor-pointer hover:bg-muted'}
            ${editingCell && !isEditing ? 'opacity-50' : ''}`}
        >
          <span className="text-sm">
            {typeof displayValue === 'number' ? displayValue.toLocaleString() : sanitizeText(displayValue)}
          </span>
          {!readOnly && !editingCell && (
            <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-60 ml-1" />
          )}
        </div>
      );

    case 'min_order_qty':
      return typeof displayValue === 'number' ? displayValue.toLocaleString() : displayValue;

    case 'client_ref_number':
      // Editable plain-text field (物料编码), modeled on the quantity case
      if (!readOnly && isEditing) {
        return (
          <Input
            type="text"
            value={editingCell?.value ?? ''}
            onChange={(e) => onCellValueChange(e.target.value)}
            onKeyDown={onCellKeyPress}
            onBlur={onCellSave}
            ref={inputRef}
            className="h-8 w-32"
          />
        );
      }
      if (readOnly) {
        return displayValue === '' ? '-' : sanitizeText(displayValue);
      }
      return (
        <div
          onClick={() => !editingCell && onCellEdit(rowIndex, 'client_ref_number', displayValue)}
          className={`flex items-center gap-1 p-1 rounded h-8 min-h-[32px] min-w-[80px]
            ${editingCell ? '' : 'cursor-pointer hover:bg-muted'}
            ${editingCell && !isEditing ? 'opacity-50' : ''}`}
        >
          <span className="text-sm">
            {displayValue === '' ? '-' : sanitizeText(displayValue)}
          </span>
          {!editingCell && (
            <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
          )}
        </div>
      );

    case 'diameter':
    case 'length':
      return typeof displayValue === 'number' ? `${displayValue}mm` : displayValue;

    case 'status':
      const statusColors = {
        'active': 'default',
        'inactive': 'destructive',
        'pending': 'secondary'
      };
      return (
        <Badge variant={statusColors[sanitizeText(displayValue)] || 'outline'}>
          {sanitizeText(displayValue)}
        </Badge>
      );

    case 'id':
    case '_id':
    case 'product_id':
    case 'code':
    case 'product_code':
      // Make product ID fields clickable to navigate to edit page
      if (onProductIdClick && product) {
        return (
          <button
            onClick={() => onProductIdClick(product)}
            className="flex items-center gap-1 p-1 rounded cursor-pointer hover:bg-muted hover:underline transition-all duration-200"
          >
            <span className="text-sm font-medium text-primary hover:underline">
              {sanitizeText(displayValue)}
            </span>
            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
          </button>
        );
      }
      // Fallback to default text if no click handler
      return typeof displayValue === 'string' ? sanitizeText(displayValue) : displayValue;

    default:
      // Sanitize all text content to prevent XSS (SECURITY FIX)
      return typeof displayValue === 'string' ? sanitizeText(displayValue) : displayValue;
  }
};

export default EditableCell;
